import Phaser from "phaser";
import { FAMILY_BODY, FAMILY_HEADING, GAME_HEIGHT, GAME_WIDTH } from "../util/constants";
import { Button } from "../ui/Button";
import { drawPanel } from "../ui/Panel";
import { battleById, BATTLES, resolveBattleForPath } from "../data/battles";
import { getMusic, MUSIC } from "../audio/Music";
import { sfxConfirm, sfxDefeat, sfxVictory } from "../audio/Sfx";
import { ensureBackdropForKey } from "../art/BackdropArt";
import { getSevenPath, loadSave, MAX_PERMITTED_DEATHS } from "../util/save";
import { ENDING_HANDOFF, resolvePostArc } from "../data/postArcs";
import { SettingsButton } from "../ui/SettingsButton";
import { ITEM_CATALOG } from "../combat/items";
import type { ItemKind } from "../combat/types";
import type { ArcId, BattleId } from "../data/contentIds";

/** The squad's best of the battle (BattleScene.bestOfBattle). */
export interface EndMvp {
  id: string;
  name: string;
  portraitId: string;
  damage: number;
  kills: number;
  heals: number;
}

interface EndArgs {
  battleId: BattleId;
  outcome: "player" | "enemy";
  mvp?: EndMvp;
}

/** The face each one wears for the award. */
const PROUD_FACE: Record<string, string> = {
  amar: "warm_half_smile", lucian: "fatherly_smile", ning: "eager_grin", maya: "soft_genuine_smile",
  leo: "cocky_smirk", ranatoli: "satisfied", veya: "wry_smile", corin: "resolute", kian: "knowing_smile",
  rose: "brisk"
};

// Post-battle arc routing lives in src/data/postArcs.ts so the
// campaign-integrity suite can assert every non-terminal battle
// actually routes into a real arc (b20/b21/b23-b28 once fell through
// the camp fallback below with no story at all).

export class EndScene extends Phaser.Scene {
  private battleId!: BattleId;
  private outcome!: "player" | "enemy";
  private mvp?: EndMvp;

  constructor() { super("EndScene"); }

  init(data: EndArgs): void {
    this.battleId = data.battleId;
    this.outcome = data.outcome;
    this.mvp = data.mvp;
  }

  // The best of the battle: their portrait slides in at the top left with
  // what they did, while the banner settles.
  private showBestOfBattle(m: EndMvp): void {
    const face = PROUD_FACE[m.portraitId];
    const key = face && this.textures.exists(`portrait:${m.portraitId}:${face}`)
      ? `portrait:${m.portraitId}:${face}`
      : this.textures.exists(`portrait:${m.portraitId}`) ? `portrait:${m.portraitId}` : null;
    const x0 = 24, y0 = 26, w = 250, h = 270;
    const parts: Phaser.GameObjects.GameObject[] = [];
    const band = this.add.graphics();
    band.fillStyle(0x0b0709, 0.9);
    band.fillPoints([
      new Phaser.Math.Vector2(x0, y0), new Phaser.Math.Vector2(x0 + w + 30, y0),
      new Phaser.Math.Vector2(x0 + w, y0 + h), new Phaser.Math.Vector2(x0, y0 + h)
    ], true);
    band.lineStyle(2, 0xd9b257, 0.9);
    band.lineBetween(x0, y0 + h, x0 + w, y0 + h);
    parts.push(band);
    if (key) {
      this.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
      const src = this.textures.get(key).getSourceImage() as HTMLImageElement;
      const img = this.add.image(x0 + w / 2 + 6, y0 + 8, key).setOrigin(0.5, 0);
      img.setCrop(0, src.height * 0.02, src.width, src.height * 0.5);
      img.setScale((h - 92) / (src.height * 0.5));
      img.setY(y0 + 8 - src.height * 0.02 * img.scaleY);
      parts.push(img);
    }
    const tag = this.add.text(x0 + 14, y0 + h - 80, "BEST OF THE BATTLE", {
      fontFamily: FAMILY_HEADING, fontSize: "13px", color: "#d9b257", letterSpacing: 4
    });
    const name = this.add.text(x0 + 14, y0 + h - 60, m.name.toUpperCase(), {
      fontFamily: FAMILY_HEADING, fontSize: "24px", color: "#f8f0d8", stroke: "#000", strokeThickness: 3
    });
    const bits = [
      m.kills ? `${m.kills} defeated` : "",
      m.damage ? `${m.damage} damage` : "",
      m.heals ? `${m.heals} healed` : ""
    ].filter(Boolean).join("  ·  ");
    const stats = this.add.text(x0 + 14, y0 + h - 26, bits, {
      fontFamily: FAMILY_BODY, fontSize: "15px", color: "#c9b896", fontStyle: "italic"
    });
    parts.push(tag, name, stats);
    for (const o of parts) {
      const g = o as unknown as Phaser.GameObjects.Components.Transform & Phaser.GameObjects.Components.Alpha;
      g.setAlpha(0);
      g.x -= 40;
      this.tweens.add({ targets: o, alpha: 1, x: g.x + 40, duration: 500, delay: 650, ease: "Cubic.easeOut" });
    }
    this.time.delayedCall(700, () => sfxConfirm());
  }

  private resolvePostArc(): ArcId | undefined {
    return resolvePostArc(this.battleId, getSevenPath(loadSave()));
  }

  create(): void {
    const rawNode = battleById(this.battleId);
    // Per-path outro text for the endgame climaxes.
    const node = rawNode ? resolveBattleForPath(rawNode, getSevenPath(loadSave())) : undefined;
    const isVictory = this.outcome === "player";

    const bdKey = node?.backdropKey ?? "bg_thuling";
    const bgKey = ensureBackdropForKey(this, bdKey);
    const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, bgKey).setDisplaySize(GAME_WIDTH, GAME_HEIGHT);
    bg.setAlpha(0.55);

    // Heavy vignette
    const v = this.add.graphics();
    v.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.7, 0.7, 0.92, 0.92);
    v.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Banner word
    const word = isVictory ? "VICTORY" : "DEFEAT";
    const accent = isVictory ? "#f4d999" : "#a83c3c";
    const stroke = isVictory ? "#1a0e04" : "#1a0404";

    const banner = this.add.text(GAME_WIDTH / 2, 180, word, {
      fontFamily: FAMILY_HEADING,
      fontSize: "96px",
      color: accent,
      stroke,
      strokeThickness: 8,
      shadow: { offsetX: 0, offsetY: 6, color: "#000", blur: 18, fill: true }
    }).setOrigin(0.5);
    banner.setAlpha(0);
    this.tweens.add({ targets: banner, alpha: 1, y: 200, duration: 700, ease: "Sine.easeOut" });
    if (isVictory && this.mvp) this.showBestOfBattle(this.mvp);

    // Battle subtitle
    if (node) {
      this.add.text(GAME_WIDTH / 2, 280, `${node.title} — ${node.subtitle}`, {
        fontFamily: FAMILY_BODY,
        fontSize: "20px",
        color: "#c9b07a"
      }).setOrigin(0.5);
    }

    // Outro panel.
    //
    // panelH was 200 originally, but the longest outros (B9 post-ravine
    // at 631 chars) wrap to ~8 lines and were overflowing into the
    // spoils row pinned to the bottom. Bumped to 280 so even worst-case
    // outros have headroom, with a gold divider separating the prose
    // from the spoils line so the eye doesn't read them as one block.
    // panelY moved up 20 to keep the buttons + stats line where they
    // already had room below.
    const panelW = 880;
    const panelH = 280;
    const panelX = (GAME_WIDTH - panelW) / 2;
    const panelY = 310;
    const pg = this.add.graphics();
    drawPanel(pg, panelX, panelY, panelW, panelH);

    const outroText = isVictory
      ? (node?.outro ?? "The squad won the battle.")
      : "The squad's line broke and they had to retreat. Their wounds will heal. Try the battle again.";

    // Reserve the bottom strip for the spoils row + divider so the outro
    // text wraps above it instead of running underneath it. Strip height
    // (50px) covers a 14px line of spoils text + padding above and below
    // the divider.
    const SPOILS_STRIP_H = 50;
    const outroAreaH = panelH - 22 - SPOILS_STRIP_H;
    const outroBody = this.add.text(panelX + 28, panelY + 22, outroText, {
      fontFamily: FAMILY_BODY,
      fontSize: "18px",
      color: "#e6e0d0",
      wordWrap: { width: panelW - 56 },
      lineSpacing: 6
    });
    // Measured safety: the longest outros (8 wrapped lines) sit within a
    // few px of the spoils divider under real font metrics. If the prose
    // doesn't fit its area, step the font down until it does — same
    // philosophy as the dialogue paginator: never let text cross into the
    // furniture below it.
    for (const size of [17, 16, 15]) {
      if (outroBody.height <= outroAreaH) break;
      outroBody.setFontSize(size);
      outroBody.setLineSpacing(5);
    }

    // Spoils — only on victory and only if the BattleNode authored a
    // rewards array. Tally by kind (so "potion ×3" reads more cleanly
    // than three identical lines), build a single comma-separated
    // line, anchor it to the bottom of the outro panel below a faint
    // gold divider so it reads as a separate block from the prose.
    if (isVictory && node?.rewards && node.rewards.length > 0) {
      const dividerY = panelY + panelH - SPOILS_STRIP_H + 4;
      const dg = this.add.graphics();
      dg.lineStyle(1, 0xc9b07a, 0.45);
      dg.lineBetween(panelX + 28, dividerY, panelX + panelW - 28, dividerY);

      const counts: Partial<Record<ItemKind, number>> = {};
      for (const k of node.rewards) counts[k] = (counts[k] ?? 0) + 1;
      const parts: string[] = [];
      for (const k of Object.keys(counts) as ItemKind[]) {
        const n = counts[k]!;
        const meta = ITEM_CATALOG[k];
        parts.push(`${meta.glyph} ${meta.name}${n > 1 ? ` ×${n}` : ""}`);
      }
      this.add.text(panelX + 28, panelY + panelH - 28, `Spoils: ${parts.join("  ")}`, {
        fontFamily: FAMILY_HEADING,
        fontSize: "14px",
        color: "#f4d999",
        stroke: "#1a0e04",
        strokeThickness: 2,
        wordWrap: { width: panelW - 56 }
      }).setOrigin(0, 0.5);
    }

    // Stats line — battle progress + remaining campaign-wide death budget.
    // The lives line dims its colour when the budget hasn't been touched
    // and brightens to the warning yellow once any deaths have landed,
    // so a no-loss player isn't visually nagged about a number that
    // reads 0/3.
    const save = loadSave();
    const completedCount = save.completedBattles.length;
    const totalPlayable = BATTLES.filter(b => b.playable).length;
    const deaths = save.squadDeaths ?? 0;
    const livesLeft = Math.max(0, MAX_PERMITTED_DEATHS - deaths);
    const livesColor = deaths === 0 ? "#5a5448" : deaths >= MAX_PERMITTED_DEATHS ? "#a83c3c" : "#c9b07a";
    this.add.text(GAME_WIDTH / 2, panelY + panelH + 14, `Battles completed: ${completedCount} / ${totalPlayable} playable`, {
      fontFamily: FAMILY_BODY,
      fontSize: "14px",
      color: "#7a7165"
    }).setOrigin(0.5);
    this.add.text(GAME_WIDTH / 2, panelY + panelH + 32, `Squad losses: ${deaths} / ${MAX_PERMITTED_DEATHS}  (lives remaining: ${livesLeft})`, {
      fontFamily: FAMILY_BODY,
      fontSize: "13px",
      color: livesColor
    }).setOrigin(0.5);

    // Buttons row — nudged down 10px to keep clearance from the taller panel.
    const btnY = GAME_HEIGHT - 80;
    const btnH = 48;
    const btnW = 220;
    const gap = 24;

    if (isVictory) {
      // On an ending handoff the ONLY way forward is Continue — see
      // ENDING_HANDOFF. Elsewhere the player keeps the map escape.
      const isEnding = ENDING_HANDOFF.has(this.battleId);
      const onContinue = () => {
        sfxConfirm();
        this.cameras.main.fadeOut(450, 0, 0, 0);
        this.cameras.main.once("camerafadeoutcomplete", () => {
          const arc = this.resolvePostArc();
          if (arc) {
            this.scene.start("StoryScene", { arcId: arc });
          } else {
            // No post arc — return to camp (the new home), not the
            // world map directly. Player can re-open the map from the
            // camp's signpost. Camp Hub commit 1.
            this.scene.start("CampScene");
          }
        });
      };

      const continueBtn = new Button(this, {
        x: isEnding ? GAME_WIDTH / 2 - btnW / 2 : GAME_WIDTH / 2 - btnW - gap / 2,
        y: btnY,
        w: btnW,
        h: btnH,
        label: "Continue ▸",
        primary: true,
        fontSize: 18,
        onClick: onContinue
      });
      void continueBtn;

      if (!isEnding) {
        const mapBtn = new Button(this, {
          x: GAME_WIDTH / 2 + gap / 2,
          y: btnY,
          w: btnW,
          h: btnH,
          label: "World Map",
          primary: false,
          fontSize: 16,
          onClick: () => {
            sfxConfirm();
            this.cameras.main.fadeOut(450, 0, 0, 0);
            this.cameras.main.once("camerafadeoutcomplete", () => this.scene.start("OverworldScene"));
          }
        });
        void mapBtn;
      }
    } else {
      const retryBtn = new Button(this, {
        x: GAME_WIDTH / 2 - btnW - gap / 2,
        y: btnY,
        w: btnW,
        h: btnH,
        label: "Try Again",
        primary: true,
        fontSize: 18,
        onClick: () => {
          sfxConfirm();
          this.cameras.main.fadeOut(450, 0, 0, 0);
          this.cameras.main.once("camerafadeoutcomplete", () =>
            this.scene.start("BattlePrepScene", { battleId: this.battleId })
          );
        }
      });

      const mapBtn = new Button(this, {
        x: GAME_WIDTH / 2 + gap / 2,
        y: btnY,
        w: btnW,
        h: btnH,
        label: "World Map",
        primary: false,
        fontSize: 16,
        onClick: () => {
          sfxConfirm();
          this.cameras.main.fadeOut(450, 0, 0, 0);
          this.cameras.main.once("camerafadeoutcomplete", () => this.scene.start("OverworldScene"));
        }
      });

      void retryBtn; void mapBtn;
    }

    // Music + sfx sting
    if (isVictory) {
      sfxVictory();
      getMusic(this).play(MUSIC.adventureAnthros, { fadeMs: 1000 });
    } else {
      sfxDefeat();
      getMusic(this).play(MUSIC.danger, { fadeMs: 1000 });
    }

    this.cameras.main.fadeIn(500, 0, 0, 0);

    // Keyboard shortcut: Enter to advance.
    this.input.keyboard?.on("keydown-ENTER", () => {
      // Trigger the primary button by simulating its click target.
      // Simpler: re-route directly.
      if (isVictory) {
        const arc = this.resolvePostArc();
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once("camerafadeoutcomplete", () =>
          arc ? this.scene.start("StoryScene", { arcId: arc }) : this.scene.start("OverworldScene")
        );
      } else {
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once("camerafadeoutcomplete", () =>
          this.scene.start("BattlePrepScene", { battleId: this.battleId })
        );
      }
    });

    new SettingsButton(this, GAME_WIDTH - 32, 32);
  }
}
